import { useEffect, useState } from 'react'
import { Grid2 as Grid, Typography } from '@mui/material'
import Paper from 'components/@extended/Paper'
import CompensationChartCard from 'components/cards/CompensationChartCard'
import PlanAllocationsCard from 'components/Plan/PlanAllocationsCards'
import type { ExpandedCompensationPlan } from 'types/comp-plan'
import type { LedgerAccount } from 'types/ledger-account'
import { createIdLabelMap, DATE_FORMAT } from 'types/periods'
import type { User } from 'types/user'
import { currencyFormatter, twoDigitPercentFormatter } from 'utils/formatting'
import useAuth from 'hooks/useAuth'
import { useCreateEngagement } from 'hooks/useEngagements'
import { analytics } from 'utils/analytics'
import { getRootOrganizationId } from 'types/user-profile'

import dayjs from 'dayjs'

function CompPlanContent({
  user,
  compPlan,
  ledgerAccounts,
}: {
  user: User | undefined
  compPlan: ExpandedCompensationPlan
  ledgerAccounts: LedgerAccount[] | undefined
}) {
  const { profile, token } = useAuth()
  const { createEngagement } = useCreateEngagement(token || '')

  const [chartViewMode, setChartViewMode] = useState<'summary' | 'breakdown'>('summary')

  const baseSalary = compPlan?.plan.baseSalary || 0
  const variablePercent = compPlan?.plan.variablePercent || 0
  const periodProration = compPlan?.plan.periodProration || 1.0
  const compensationModel = compPlan?.plan.compensationModel || 'BASE_PLUS'
  const variableCap = compPlan?.plan.variableCap || 0

  const periodIdLabelMap = createIdLabelMap(compPlan.period)
  const periodLabel = periodIdLabelMap[compPlan.plan.periodId] || ''

  const baseCompensation = baseSalary * periodProration
  const variableCompensation = baseSalary * variablePercent * periodProration
  const maxVariableCompensation = baseSalary * variableCap * variablePercent * periodProration
  const onTargetTotal =
    compensationModel === 'BASE_PLUS'
      ? baseCompensation + variableCompensation
      : baseSalary * periodProration * (1 - variablePercent) + variableCompensation
  const maxTotal =
    compensationModel === 'BASE_PLUS'
      ? baseCompensation + maxVariableCompensation
      : baseSalary * periodProration * (1 - variablePercent) + maxVariableCompensation

  // Calculate allocation totals for the chart - flatten all rows
  const allAllocations = compPlan.plan.variableAllocations.flatMap((group) =>
    group.rows
      .filter((row) => !row.disabled && (row.allocationPercentage || 0) > 0)
      .map((row) => ({
        label: row.label,
        value: baseSalary * variablePercent * (row.allocationPercentage || 0) * periodProration,
      }))
  )

  // Breakdown shows just variable allocations (no base)
  const chartSeries = allAllocations.map((a) => a.value)
  const chartLabels = allAllocations.map((a) => a.label)

  useEffect(() => {
    if (compPlan.plan && user) {
      if (profile) {
        const rootOrgId = getRootOrganizationId(profile)
        createEngagement({
          userId: profile.user.id,
          rootOrganizationId: rootOrgId,
          eventType: 'compensation_plan_view',
          viewedRecordId: compPlan.plan.id || null,
          viewedRecordUserId: user.id,
          viewedRecordType: 'compensation_plan',
          eventData: null,
        })
      }
      analytics.track('employee_incentive_dashboard_viewed', {
        userId: profile?.user.id,
        viewedForUserId: user?.id,
      })
    }
  }, [status, user?.id])

  return (
    <Grid container spacing={3}>
      {/* Left section: Employee and Compensation tiles with table below */}
      <Grid size={{ xs: 12, md: 9 }}>
        <Grid container spacing={3}>
          {/* Employee Information Card - Full width, single row */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Employee Name
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {user?.firstName} {user?.lastName}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Position
                  </Typography>
                  <Typography variant="body1">{user?.position}</Typography>
                </Grid>

                <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Compensation Period
                  </Typography>
                  <Typography variant="body1">{periodLabel}</Typography>
                </Grid>

                <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Effective Date
                  </Typography>
                  <Typography variant="body1">{dayjs.utc(compPlan.plan.effectiveDate).format(DATE_FORMAT)}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Chart - Mobile only, shown after employee details */}
          <Grid size={{ xs: 12 }} sx={{ display: { xs: 'block', md: 'none' } }}>
            <CompensationChartCard
              title="Compensation Breakdown"
              showToggle={true}
              viewMode={chartViewMode}
              onViewModeChange={setChartViewMode}
              customSeries={chartViewMode === 'summary' ? [baseCompensation, variableCompensation] : chartSeries}
              customLabels={chartViewMode === 'summary' ? ['Base', 'Variable'] : chartLabels}
            />
          </Grid>

          {/* Compensation Details - 3 Outlined Cards */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'center',
                height: 140,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ height: '20px' }}>
                Base Compensation
              </Typography>
              <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h4" fontWeight="bold">
                  {currencyFormatter.format(baseCompensation)}
                </Typography>
              </div>
              <div style={{ height: '28px' }}></div>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'center',
                height: 140,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ height: '20px' }}>
                Variable Compensation
              </Typography>
              <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h4" fontWeight="bold">
                  {currencyFormatter.format(variableCompensation)}
                </Typography>
              </div>
              <div style={{ height: '28px' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  ({twoDigitPercentFormatter.format(variablePercent)})
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Up to {currencyFormatter.format(maxVariableCompensation)}
                </Typography>
              </div>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'center',
                height: 140,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ height: '20px' }}>
                On-Target Total
              </Typography>
              <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {currencyFormatter.format(onTargetTotal)}
                </Typography>
              </div>
              <div style={{ height: '28px' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Up to {currencyFormatter.format(maxTotal)}
                </Typography>
              </div>
            </Paper>
          </Grid>

          {/* Allocations Table - full width within the 8-column container */}
          <Grid size={{ xs: 12 }}>
            <PlanAllocationsCard key="plan-allocations-card" plan={compPlan} />
          </Grid>
        </Grid>
      </Grid>

      {/* Right section: Compensation Chart - 3 columns, desktop only */}
      <Grid size={{ xs: 12, md: 3 }} sx={{ display: { xs: 'none', md: 'block' } }}>
        <CompensationChartCard
          title="Compensation Breakdown"
          showToggle={true}
          viewMode={chartViewMode}
          onViewModeChange={setChartViewMode}
          customSeries={chartViewMode === 'summary' ? [baseCompensation, variableCompensation] : chartSeries}
          customLabels={chartViewMode === 'summary' ? ['Base', 'Variable'] : chartLabels}
        />
      </Grid>
    </Grid>
  )
}

export default CompPlanContent
